import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const LOGO_URL = 'https://public.readdy.ai/ai/img_res/fe6b96b6-a4b6-4cea-93e9-3d0dcbee93ab.png';

const anchorLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Services', href: '#services' },
  { label: 'Products', href: '#products' },
  { label: 'About Us', href: '#about' },
  { label: 'Contact', href: '#contact' },
  { label: 'FAQ', href: '#faq' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAnchor = (href: string) => {
    setMenuOpen(false);
    if (isHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/${href}`);
    }
  };

  const linkClass = `text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
    scrolled || !isHome ? 'text-stone-700 hover:text-[#1B4332]' : 'text-white hover:text-stone-200'
  }`;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled || !isHome ? 'bg-white border-b border-stone-200' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src={LOGO_URL}
              alt="BritFit Mobile Tyres Logo"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <ul className="hidden md:flex items-center gap-7">
            {anchorLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleAnchor(link.href); }}
                  className={linkClass}
                >
                  {link.label}
                </a>
              </li>
            ))}
            <li>
              {isHome ? (
                <a
                  href="#locations"
                  onClick={(e) => { e.preventDefault(); handleAnchor('#locations'); }}
                  className={linkClass}
                >
                  Locations
                </a>
              ) : (
                <Link to="/locations" className={linkClass}>
                  Locations
                </Link>
              )}
            </li>
          </ul>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+441234567890"
              className="flex items-center gap-2 bg-[#1B4332] text-white text-sm font-semibold px-5 py-2.5 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#145228] transition-colors"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm" />
              </span>
              Call Now
            </a>
          </div>

          {/* Hamburger */}
          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${
              scrolled || !isHome ? 'text-stone-700' : 'text-white'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-300 overflow-hidden bg-white border-b border-stone-200 ${
            menuOpen ? 'max-h-[28rem] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <ul className="flex flex-col px-4 py-4 gap-1">
            {anchorLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleAnchor(link.href); }}
                  className="block py-2.5 px-3 text-stone-700 text-sm font-medium rounded-lg hover:bg-stone-50 cursor-pointer transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
            <li>
              {isHome ? (
                <a
                  href="#locations"
                  onClick={(e) => { e.preventDefault(); handleAnchor('#locations'); }}
                  className="block py-2.5 px-3 text-stone-700 text-sm font-medium rounded-lg hover:bg-stone-50 cursor-pointer transition-colors"
                >
                  Locations
                </a>
              ) : (
                <Link
                  to="/locations"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2.5 px-3 text-stone-700 text-sm font-medium rounded-lg hover:bg-stone-50 cursor-pointer transition-colors"
                >
                  Locations
                </Link>
              )}
            </li>
            <li className="pt-2">
              <a
                href="tel:+441234567890"
                className="flex items-center justify-center gap-2 bg-[#1B4332] text-white text-sm font-semibold px-5 py-2.5 rounded-full cursor-pointer"
              >
                <i className="ri-phone-line" /> Call Now
              </a>
            </li>
          </ul>
        </div>
      </nav>
    </>
  );
}
